import type { NewProfileSection, PublicProfileErrors } from '@/generated-api'
import type { IProfileError } from '@/interfaces/IProfileError'
import type { IDrawerList } from '@/src/interfaces/components/IDrawerList'
import type { IDropdownItem } from '@/src/interfaces/components/IDropdownComponent'
import { useAppStore, useLocalizationStore, useProfileStore } from '@/stores'
import { generateMonths } from './utilities'

const populateSharedErrors = (profileErrors: PublicProfileErrors): IProfileError[] => {
  const localizationStore = useLocalizationStore()

  const sharedErrors: IProfileError[] = []

  if (profileErrors.emailError) {
    sharedErrors.push({
      text: localizationStore.translate('profile_PublishError_Email'),
      drawer: 'personaldata'
    })
  }

  if (profileErrors.competenceError) {
    sharedErrors.push({
      text: localizationStore.translate('profile_PublishError_Competences'),
      drawer: 'competences'
    })
  }

  if (profileErrors.proxyError) {
    sharedErrors.push({ text: localizationStore.translate('profile_PublishError_Proxy') })
  }

  return sharedErrors
}

export const populateNetworkErrors = (profileErrors: PublicProfileErrors): IProfileError[] => {
  const localizationStore = useLocalizationStore()

  let errors: IProfileError[] = []

  errors = populateSharedErrors(profileErrors)

  if (profileErrors.networkModuleError) {
    errors.push({
      text: localizationStore.translate('profile_PublishNetworkError_Module'),
      link: profileErrors.networkModuleLink
    })
  }

  if (profileErrors.summaryError) {
    errors.push({
      text: localizationStore.translate('profile_PublishNetworkError_Summary'),
      drawer: 'experience'
    })
  }

  return errors
}

export const populateJobProfileErros = (profileErrors: PublicProfileErrors): IProfileError[] => {
  const localizationStore = useLocalizationStore()

  let errors: IProfileError[] = []

  errors = populateSharedErrors(profileErrors)

  if (profileErrors.phoneError) {
    errors.push({
      text: localizationStore.translate('profile_PublishError_Phone'),
      drawer: 'personaldata'
    })
  }

  if (profileErrors.jobTypeError) {
    errors.push({
      text: localizationStore.translate('profile_PublishError_JobTypes'),
      drawer: 'nextjob'
    })
  }

  if (profileErrors.mobilityError) {
    errors.push({
      text: localizationStore.translate('profile_PublishError_Mobility'),
      drawer: 'nextjob'
    })
  }

  if (profileErrors.addressError) {
    errors.push({
      text: localizationStore.translate('profile_PublishError_AddressPhoneCity'),
      drawer: 'personaldata'
    })
  }

  if (profileErrors.publicProfileError) {
    errors.push({
      text: localizationStore.translate('profile_PublishError_PublicProfileNotAllowed')
    })
  }

  return errors
}

export const populateDrawerList = (): IDrawerList[] => {
  const appStore = useAppStore()
  const profileStore = useProfileStore()

  const jobProfileSection = profileStore.jobProfileSection

  const networkSection = profileStore.networkSection

  const profileData = appStore.content.profileData

  const jobProfileSectionItem = {
    ...jobProfileSection,
    ...createIcon(jobProfileSection?.sectionType),
    title: jobProfileSection?.header ?? '',
    description: jobProfileSection?.listSubtitle,
    type: jobProfileSection?.sectionType,
    badge: createBadge(
      profileStore.jobProfileSectionPublished ?? false,
      jobProfileSection?.visible ?? '',
      jobProfileSection?.notVisible ?? ''
    )
  }

  const networkSectionItem = {
    ...networkSection,
    ...createIcon(networkSection?.sectionType),
    title: networkSection?.header ?? '',
    description: networkSection?.listSubtitle,
    type: networkSection?.sectionType,
    badge: createBadge(
      profileStore.networkSectionPublished ?? false,
      networkSection?.member ?? '',
      networkSection?.notAMember ?? ''
    )
  }

  // Its possible to toggle jobProfileSection and networkProfileSection on or off in umbraco
  const jobNetworkSections: IDrawerList[] =
    profileData.showJobProfileSection || profileData.showNetworkProfileSection
      ? [
          {
            title: profileStore.profileData?.editProfileSectionHeaderJobProfile ?? '',
            list: [
              ...(profileData.showJobProfileSection ? [jobProfileSectionItem] : []),
              ...(profileData.showNetworkProfileSection ? [networkSectionItem] : [])
            ]
          }
        ]
      : []

  return [
    ...jobNetworkSections,
    {
      title: profileStore.profileData?.editProfileSectionHeaderEditProfile ?? '',
      list: profileData.sections.map((section: NewProfileSection) => {
        return {
          ...section,
          ...createIcon(section.sectionType),
          type: section.sectionType
        }
      })
    }
  ]
}

const createIcon = (type: string | undefined): { icon: string; color: string } => {
  switch (type) {
    case 'personaldata':
      return { icon: 'id-card', color: 'blue' }
    case 'experience':
      return { icon: 'pencil', color: 'purple' }
    case 'competences':
      return { icon: 'stars', color: 'emerald' }
    case 'nextjob':
      return { icon: 'briefcase', color: 'orange' }
    case 'cv':
      return { icon: 'paperclip', color: 'gray' }
    case 'publishjobprofile':
      return { icon: 'handshake', color: 'gray' }
    case 'network':
      return { icon: 'chart-network', color: 'gray' }
    default:
      return { icon: 'user', color: 'gray' }
  }
}

export const createBadge = (
  condition: boolean,
  activeText: string,
  inactiveText: string
): { text: string; type: string } => {
  return {
    text: condition ? activeText : inactiveText,
    type: condition ? 'green' : 'red'
  }
}

export const profileDropdown = (): IDropdownItem[] => {
  const localizationStore = useLocalizationStore()

  return [
    {
      id: 'item_setpublic',
      icon: 'eye-slash',
      name: localizationStore.translate('profile_Hide_On_Profile'),
      action: 'setPublic'
    },
    {
      id: 'item_setpublic',
      icon: 'eye',
      name: localizationStore.translate('profile_Show_On_Profile'),
      action: 'setPublic'
    },
    {
      id: 'item_edit',
      icon: 'pencil',
      name: localizationStore.translate('general_Edit'),
      action: 'edit'
    },
    {
      id: 'item_delete',
      icon: 'trash',
      name: localizationStore.translate('general_Delete'),
      action: 'delete'
    }
  ]
}

export const formatProfileDate = (start: string, end?: string): string => {
  if (start !== '' && start != undefined) {
    const localizationStore = useLocalizationStore()
    const months = generateMonths()

    const splittedStartDate = start.split('-')
    const splittedEndDate = end !== undefined && end !== '' ? end.split('-') : undefined

    const formatStartDate = `${months.find((month) => month.id === splittedStartDate[1])?.name} ${splittedStartDate[0]}`
    const formatEndDate =
      splittedEndDate !== undefined
        ? `${months.find((month) => month.id === splittedEndDate[1])?.name} ${splittedEndDate[0]}`
        : localizationStore.translate('general_Now')

    return `${formatStartDate} - ${formatEndDate}`
  } else {
    return ''
  }
}
